/**
 * Read adapters for stored ranking payloads (plots plan I; contract in
 * `../contract.ts`, shapes in docs/deck-design-system/plots/read-adapters.md).
 *
 * Shape only: they rename and regroup what Python already stamped. The legacy
 * readers keep today's rules for stored payloads that predate the stamps: a
 * deck ranking slide stored before `plot` (the leader fills the track, the
 * stored ordinal rank, today's `1 dp unless whole` figure) and a summary
 * `per_item_rank` / `dim_rank` card stored before `rank`/`scale`/`topIds`
 * (the order-based rank `toPodiumItems` assigned, the largest value fills the
 * track, rank 1 crowned unless `isTie`, the stored `valueLabel` printed as
 * stored). Stored shapes: `tests/fixtures/plots/ranking-bars-*.stored.v1.json`.
 */

import { RankItem, RankSeries } from '@digital-twins/slide-schemas/generated/plot-types'
import { PlotReadAdapter, StoredPlotPayload } from '../contract'

export type RankingBarsPayload = StoredPlotPayload<'ranking-bars'>

/** The deck `ranking` slide as stored (`ranking.schema.json`), plot optional. */
export interface StoredRankingSlide {
  type: 'ranking'
  bars: Array<{
    id: string
    label: string
    value: number
    rank: number
    count?: number | null
    isTop?: boolean
    imageUrl?: string | null
  }>
  plot?: { kind: string; data: unknown } | null
}

interface StoredRankingCardItem {
  id: string
  label: string
  tag?: string | null
  imageUrl?: string | null
  previewKind?: 'image' | 'snapshot' | 'video' | null
  respondentCopy?: string | null
  value: number
  valueLabel?: string | null
  isEscape?: boolean
  rank?: number | null
}

/** The summary card `per_item_rank` / `dim_rank` payload as stored (`RankingPayload`). */
export interface StoredRankingCard {
  shape: 'per_item_rank' | 'dim_rank'
  items: Array<StoredRankingCardItem>
  series?: Array<{
    id: string
    label: string
    items: Array<StoredRankingCardItem>
    scoreLabel?: string | null
    unit?: string | null
    topIds?: Array<string> | null
    isTie?: boolean | null
  }>
  defaultSeriesId?: string | null
  scoreLabel?: string | null
  unit?: string | null
  baseSize?: number | null
  isTie?: boolean
  topIds?: Array<string> | null
  decimals?: number | null
  figure?: string | null
  scale?: { min: number; max: number } | null
}

/** A read: the plot payload, whether it came through a legacy reader, and how to draw it. */
export interface RankingBarsRead {
  payload: RankingBarsPayload
  isLegacy: boolean
  /** Legacy readers draw every bar in the primary colour, as today. */
  highlight: 'top' | 'none'
  /** Legacy only: today's printed figure; stamped payloads print `value` at `decimals`. */
  formatValue?: (item: RankItem) => string | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isRankingPlot(value: unknown): value is RankingBarsPayload {
  return isRecord(value) && value.kind === 'ranking-bars' && isRecord(value.data) && Array.isArray(value.data.items)
}

function maxValue(values: Array<number>): number {
  return Math.max(...values, 0) || 1
}

/** A deck ranking slide built with `plot`: drawn as stamped. */
export const deckSlideRankingAdapter: PlotReadAdapter<'ranking-bars', StoredRankingSlide> = {
  kind: 'ranking-bars',
  source: { surface: 'deck-slide', shape: 'ranking' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredRankingSlide {
    return isRecord(stored) && stored.type === 'ranking' && isRankingPlot(stored.plot)
  },
  read(stored: StoredRankingSlide): RankingBarsPayload {
    const plot = stored.plot as RankingBarsPayload
    return { kind: 'ranking-bars', data: { ...plot.data, labels: plot.data.labels ?? {} } }
  },
}

/** Today's slide figure: whole numbers bare, else one decimal. */
function legacySlideFigure(value: number): string {
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)
}

/** A deck ranking slide stored before `plot`: today's reader, moved here. */
export const legacyDeckSlideRankingAdapter: PlotReadAdapter<'ranking-bars', StoredRankingSlide> = {
  kind: 'ranking-bars',
  source: { surface: 'deck-slide', shape: 'ranking' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredRankingSlide {
    return isRecord(stored) && stored.type === 'ranking' && Array.isArray(stored.bars)
  },
  read(stored: StoredRankingSlide): RankingBarsPayload {
    const bars = stored.bars ?? []
    return {
      kind: 'ranking-bars',
      data: {
        items: bars.map((bar) => ({
          id: bar.id,
          label: bar.label,
          value: bar.value,
          rank: bar.rank,
          lowBase: false,
          colorSlot: null,
          imageUrl: bar.imageUrl ?? null,
        })),
        // Today's badge colours follow the stored ordinal rank: rank 1 wore the crown.
        topIds: bars.filter((bar) => bar.rank === 1).map((bar) => bar.id),
        tie: false,
        // Today the leader fills the track (bar = value / largest value).
        scale: { min: 0, max: maxValue(bars.map((bar) => bar.value)) },
        unit: 'weighted_score',
        figure: 'weighted_score',
        decimals: 1,
        basis: 'respondents',
        labels: {},
      },
    }
  },
}

/** Read a stored deck ranking slide: stamped when it carries `plot`, else legacy. */
export function readRankingSlide(stored: StoredRankingSlide): RankingBarsRead {
  if (deckSlideRankingAdapter.accepts(stored)) {
    return { payload: deckSlideRankingAdapter.read(stored), isLegacy: false, highlight: 'top' }
  }
  return {
    payload: legacyDeckSlideRankingAdapter.read(stored),
    isLegacy: true,
    highlight: 'none',
    formatValue: (item) => legacySlideFigure(item.value),
  }
}

function cardItem(item: StoredRankingCardItem, rank: number): RankItem {
  return {
    id: item.id,
    label: item.label,
    tag: item.tag ?? null,
    value: item.value,
    rank,
    lowBase: false,
    colorSlot: null,
    imageUrl: item.imageUrl ?? null,
    previewKind: item.previewKind ?? null,
    ...(item.respondentCopy ? { respondentCopy: item.respondentCopy } : {}),
    ...(item.isEscape ? { isEscape: true } : {}),
  }
}

function isStampedCard(card: StoredRankingCard): boolean {
  return (
    card.scale != null &&
    Array.isArray(card.topIds) &&
    typeof card.decimals === 'number' &&
    card.items.every((item) => typeof item.rank === 'number') &&
    (card.series ?? []).every((series) => Array.isArray(series.topIds))
  )
}

function cardFigure(figure: string | null | undefined): RankingBarsPayload['data']['figure'] {
  return figure === 'share' || figure === 'mean' || figure === 'weighted_score' ? figure : 'mean'
}

/** A summary ranking card built with the stamps: drawn as stamped. */
export const summaryCardRankingAdapter: PlotReadAdapter<'ranking-bars', StoredRankingCard> = {
  kind: 'ranking-bars',
  source: { surface: 'summary-card', shape: 'per_item_rank' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredRankingCard {
    return (
      isRecord(stored) &&
      (stored.shape === 'per_item_rank' || stored.shape === 'dim_rank') &&
      Array.isArray(stored.items) &&
      isStampedCard(stored as unknown as StoredRankingCard)
    )
  },
  read(card: StoredRankingCard): RankingBarsPayload {
    const series: Array<RankSeries> = (card.series ?? []).map((entry) => ({
      id: entry.id,
      label: entry.label,
      items: entry.items.map((item) => cardItem(item, item.rank as number)),
      unit: entry.unit ?? null,
      figureLabel: entry.scoreLabel ?? null,
      topIds: entry.topIds ?? [],
      tie: Boolean(entry.isTie),
    }))
    return {
      kind: 'ranking-bars',
      data: {
        items: card.items.map((item) => cardItem(item, item.rank as number)),
        ...(series.length > 0 ? { series, defaultSeriesId: card.defaultSeriesId ?? series[0].id } : {}),
        topIds: card.topIds ?? [],
        tie: Boolean(card.isTie),
        scale: card.scale as { min: number; max: number },
        unit: card.unit ?? '',
        figure: cardFigure(card.figure),
        figureLabel: card.scoreLabel ?? null,
        decimals: card.decimals as number,
        basis: 'respondents',
        base: card.baseSize ?? null,
        labels: {},
      },
    }
  },
}

/**
 * Today's card rank (`toPodiumItems`, moved here from the summary registry):
 * entrants numbered by stored order, escapes out of the race.
 */
function legacyCardItems(items: Array<StoredRankingCardItem>): Array<RankItem> {
  let entrantRank = 0
  return items.map((item) => cardItem(item, item.isEscape ? 0 : ++entrantRank))
}

function legacyTop(items: Array<RankItem>): Array<string> {
  const first = items.find((item) => !item.isEscape)
  return first ? [first.id] : []
}

/** A summary ranking card stored before the stamps: today's reader, moved here. */
export const legacySummaryCardRankingAdapter: PlotReadAdapter<'ranking-bars', StoredRankingCard> = {
  kind: 'ranking-bars',
  source: { surface: 'summary-card', shape: 'per_item_rank' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredRankingCard {
    return isRecord(stored) && (stored.shape === 'per_item_rank' || stored.shape === 'dim_rank') && Array.isArray(stored.items)
  },
  read(card: StoredRankingCard): RankingBarsPayload {
    const items = legacyCardItems(card.items)
    const series: Array<RankSeries> = (card.series ?? []).map((entry) => {
      const seriesItems = legacyCardItems(entry.items)
      return {
        id: entry.id,
        label: entry.label,
        items: seriesItems,
        unit: entry.unit ?? null,
        figureLabel: entry.scoreLabel ?? null,
        // Today one payload-level tie flag governed every series.
        topIds: legacyTop(seriesItems),
        tie: Boolean(card.isTie),
      }
    })
    const everyValue = [...card.items, ...(card.series ?? []).flatMap((entry) => entry.items)].map((item) => item.value)
    return {
      kind: 'ranking-bars',
      data: {
        items,
        ...(series.length > 0 ? { series, defaultSeriesId: card.defaultSeriesId ?? series[0].id } : {}),
        topIds: legacyTop(items),
        tie: Boolean(card.isTie),
        // Today the largest value on the card fills the track.
        scale: { min: 0, max: maxValue(everyValue) },
        unit: card.unit ?? '',
        figure: 'mean',
        figureLabel: card.scoreLabel ?? null,
        decimals: 1,
        basis: 'respondents',
        base: card.baseSize ?? null,
        labels: {},
      },
    }
  },
}

/** Read a stored summary ranking card: stamped when it carries the stamps, else legacy. */
export function readRankingCard(card: StoredRankingCard): RankingBarsRead {
  // `accepts` is a type guard; keep the stored shape for the legacy branch.
  const legacy: StoredRankingCard = card
  if (summaryCardRankingAdapter.accepts(card)) {
    return { payload: summaryCardRankingAdapter.read(card), isLegacy: false, highlight: 'top' }
  }
  // An id recurs across series with other values: a stored row is found by id and value.
  const keyOf = (id: string, value: number) => `${id}\u0000${value}`
  const stored = new Map(
    [...legacy.items, ...(legacy.series ?? []).flatMap((entry) => entry.items)].map((item) => [keyOf(item.id, item.value), item]),
  )
  return {
    payload: legacySummaryCardRankingAdapter.read(legacy),
    isLegacy: true,
    highlight: 'none',
    // The stored figure when the card names no unit; a caller that owns the
    // unit formatter (the summary tab's `formatAbsoluteScore`) replaces it.
    formatValue: (item) => stored.get(keyOf(item.id, item.value))?.valueLabel ?? String(item.value),
  }
}

/** A quick-study result as stored: the ranking plot Python attached, when present. */
export interface StoredQuickStudyRanking {
  ranking_plot?: { kind: string; data: unknown } | null
}

/** A quick-study ranking insight built with `ranking_plot`: drawn as stamped. */
export const quickStudyRankingAdapter: PlotReadAdapter<'ranking-bars', StoredQuickStudyRanking> = {
  kind: 'ranking-bars',
  source: { surface: 'quick-study', shape: 'ranking' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredQuickStudyRanking {
    return isRecord(stored) && isRankingPlot(stored.ranking_plot)
  },
  read(stored: StoredQuickStudyRanking): RankingBarsPayload {
    const plot = stored.ranking_plot as RankingBarsPayload
    // The first-choice view rides as a stamped alternative (W7 Q5): carried as stamped.
    const alternatives = plot.alternatives?.length ? { alternatives: plot.alternatives } : {}
    return { kind: 'ranking-bars', data: { ...plot.data, labels: plot.data.labels ?? {} }, ...alternatives }
  },
}

/** Every ranking-bars adapter, one per stored shape. */
export const RANKING_BARS_READ_ADAPTERS = [
  deckSlideRankingAdapter,
  legacyDeckSlideRankingAdapter,
  summaryCardRankingAdapter,
  legacySummaryCardRankingAdapter,
  quickStudyRankingAdapter,
] as const
